import { useState, useEffect } from 'react';
import { AdminLocationInfo, CatalogEntry, getAdminLocations, getCatalog } from '../api/adminApi';
import CountryDrawer from '../components/CountryDrawer';
import AddCountryModal from '../components/AddCountryModal';
import styles from './CountriesPage.module.css';

import { flagEmoji } from '../utils/flag';

const METHOD_LABELS: Record<string, string> = {
  MOON_SIGHTING: 'Moon Sighting',
  CALCULATION: 'Calculation',
  BOTH: 'Both',
};

const REGION_ORDER = [
  'Arabian Peninsula',
  'Levant & Iraq',
  'North Africa',
  'Sub-Saharan Africa',
  'Türkiye',
  'Iran',
  'Balkans & Europe',
  'Central Asia',
  'South Asia',
  'Southeast Asia',
  'Western Countries',
  'Other',
];

type Tab = 'all' | 'enabled' | 'disabled';

interface CountriesPageProps {
  onLocationChanged?: () => void;
}

export default function CountriesPage({ onLocationChanged }: CountriesPageProps) {
  const [locations, setLocations] = useState<AdminLocationInfo[]>([]);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [drawer, setDrawer] = useState<AdminLocationInfo | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');

  async function loadData() {
    setLoading(true);
    try {
      const [locs, cat] = await Promise.all([getAdminLocations(), getCatalog()]);
      setLocations(locs);
      setCatalog(cat);
    } catch {
      setError('Failed to load countries');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  const filtered = locations.filter(l => {
    const matchesTab = tab === 'all' || (tab === 'enabled' ? l.enabled : !l.enabled);
    const matchesQuery = !query || l.name.toLowerCase().includes(query.toLowerCase()) ||
      l.code.toLowerCase().includes(query.toLowerCase()) ||
      (l.region || '').toLowerCase().includes(query.toLowerCase());
    return matchesTab && matchesQuery;
  });

  const enabledCount = locations.filter(l => l.enabled).length;
  const disabledCount = locations.filter(l => !l.enabled).length;

  // Group by region
  const grouped = new Map<string, AdminLocationInfo[]>();
  for (const loc of filtered) {
    const region = loc.region || 'Other';
    if (!grouped.has(region)) grouped.set(region, []);
    grouped.get(region)!.push(loc);
  }
  const sortedRegions = REGION_ORDER.filter(r => grouped.has(r));
  const extraRegions = [...grouped.keys()].filter(r => !REGION_ORDER.includes(r)).sort();

  function handleDrawerSaved(updated: AdminLocationInfo) {
    setLocations(prev => prev.map(l => l.code === updated.code ? updated : l));
    setDrawer(updated);
    onLocationChanged?.();
  }

  function handleCountryAdded(loc: AdminLocationInfo) {
    setLocations(prev => [...prev, loc]);
    setCatalog(prev => prev.filter(c => c.code !== loc.code));
    setShowAdd(false);
    setDrawer(loc);
    onLocationChanged?.();
  }

  return (
    <>
      <main className={styles.main}>
        {/* Stats row */}
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{locations.length}</span>
            <span className={styles.statLabel}>Configured</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue} style={{ color: '#16a34a' }}>{enabledCount}</span>
            <span className={styles.statLabel}>Enabled</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue} style={{ color: '#94a3b8' }}>{disabledCount}</span>
            <span className={styles.statLabel}>Disabled</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            {(['all', 'enabled', 'disabled'] as Tab[]).map(t => (
              <button
                key={t}
                className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'all' ? 'All' : t === 'enabled' ? 'Enabled' : 'Disabled'}
                <span className={styles.tabCount}>
                  {t === 'all' ? locations.length : t === 'enabled' ? enabledCount : disabledCount}
                </span>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className={styles.search}
              type="text"
              placeholder="Search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button className={styles.addCountryBtn} onClick={() => setShowAdd(true)} disabled={catalog.length === 0}>
              + Add Country {catalog.length > 0 && <span className={styles.catalogBadge}>{catalog.length}</span>}
            </button>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {loading ? (
          <div className={styles.loadingWrap}><div className={styles.spinner} /></div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            {locations.length === 0
              ? <><p>No countries configured yet.</p><button className={styles.linkBtn} onClick={() => setShowAdd(true)}>Add your first country →</button></>
              : <p>No countries match your search.</p>
            }
          </div>
        ) : (
          <div className={styles.groups}>
            {[...sortedRegions, ...extraRegions].map(region => (
              <div key={region} className={styles.group}>
                <div className={styles.groupHeader}>
                  <span className={styles.groupName}>{region}</span>
                  <span className={styles.groupCount}>{grouped.get(region)!.length}</span>
                </div>
                <div className={styles.cardGrid}>
                  {grouped.get(region)!.map(loc => (
                    <CountryMiniCard key={loc.code} loc={loc} onClick={() => setDrawer(loc)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {drawer && (
        <CountryDrawer
          location={drawer}
          onClose={() => setDrawer(null)}
          onSaved={handleDrawerSaved}
        />
      )}

      {showAdd && (
        <AddCountryModal
          catalog={catalog}
          onClose={() => setShowAdd(false)}
          onAdded={handleCountryAdded}
        />
      )}
    </>
  );
}

function CountryMiniCard({ loc, onClick }: { loc: AdminLocationInfo; onClick: () => void }) {
  const method = loc.method;
  return (
    <div
      className={`${styles.miniCard} ${!loc.enabled ? styles.miniCardDisabled : ''}`}
      onClick={onClick}
    >
      <div className={styles.miniCardTop}>
        <span className={styles.miniFlag}>{flagEmoji(loc.code)}</span>
        <span className={`${styles.miniStatus} ${loc.enabled ? styles.miniStatusOn : styles.miniStatusOff}`} />
      </div>
      <span className={styles.miniName}>{loc.name}</span>
      <span className={`${styles.miniMethod} ${method === 'CALCULATION' ? styles.methodCalc : method === 'BOTH' ? styles.methodBoth : styles.methodSighting}`}>
        {METHOD_LABELS[method] || method}
      </span>
    </div>
  );
}
